import { FormEvent, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import {
  exportAdminGenerationFeedback,
  getAdminGenerationFeedbackSummary,
  listAdminGenerationFeedback,
} from '@/services/templateSystemApi';
import { getUserFriendlyMessage } from '@/services/apiError';
import PageLoading from '@/components/common/PageLoading';
import ContentApiErrorState, { ContentLoadError } from '@/components/common/ContentErrorState';
import FormMessage from '@/components/common/FormMessage';

interface FeedbackFilters {
  dateFrom?: string;
  dateTo?: string;
  domain?: string;
  rating?: 'like' | 'dislike' | '';
  templateId?: string;
  providerId?: string;
  modelId?: string;
  page: number;
  pageSize: number;
}

const defaultFilters: FeedbackFilters = {
  page: 1,
  pageSize: 20,
  rating: '',
};

export default function AdminGenerationFeedbackPage() {
  const [filters, setFilters] = useState<FeedbackFilters>(defaultFilters);
  const [errorMessage, setErrorMessage] = useState('');

  const apiFilters = useMemo(() => {
    return {
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      domain: filters.domain || undefined,
      rating: filters.rating || undefined,
      templateId: filters.templateId || undefined,
      providerId: filters.providerId || undefined,
      modelId: filters.modelId || undefined,
      page: filters.page,
      pageSize: filters.pageSize,
    };
  }, [filters]);

  const summaryQuery = useQuery({
    queryKey: queryKeys.adminFeedback.summary(apiFilters),
    queryFn: () => getAdminGenerationFeedbackSummary(apiFilters),
    retry: 1,
  });

  const listQuery = useQuery({
    queryKey: queryKeys.adminFeedback.list(apiFilters),
    queryFn: () => listAdminGenerationFeedback(apiFilters),
    retry: 1,
  });

  const exporting = async (format: 'json' | 'csv') => {
    setErrorMessage('');
    try {
      const payload = await exportAdminGenerationFeedback({
        ...apiFilters,
        format,
      });
      if (format === 'csv') {
        const blob = new Blob([payload as string], { type: 'text/csv;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `generation-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
        return;
      }
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `generation-feedback-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      setErrorMessage(getUserFriendlyMessage(error, '导出失败'));
    }
  };

  if (summaryQuery.isLoading && listQuery.isLoading) {
    return <PageLoading message='反馈分析加载中…' />;
  }

  if (summaryQuery.isError || listQuery.isError) {
    return (
      <div className='max-w-[1280px] mx-auto px-6 py-12'>
        <ContentApiErrorState
          error={summaryQuery.error ?? listQuery.error}
          onRetry={() => {
            void summaryQuery.refetch();
            void listQuery.refetch();
          }}
        />
      </div>
    );
  }

  const summary = summaryQuery.data;
  const list = listQuery.data;

  if (!summary || !list) {
    return (
      <div className='max-w-[1280px] mx-auto px-6 py-12'>
        <ContentLoadError
          onRetry={() => {
            void summaryQuery.refetch();
            void listQuery.refetch();
          }}
        />
      </div>
    );
  }

  return (
    <div className='max-w-[1280px] mx-auto px-6 py-12 space-y-6'>
      <div className='flex items-center justify-between gap-3'>
        <div>
          <h1 className='font-semibold text-[2rem] leading-tight tracking-tight text-apple-near-black'>生成反馈分析</h1>
          <p className='text-[0.9375rem] text-apple-gray mt-1'>查看模板生成质量反馈并导出数据用于提示词优化。</p>
        </div>
        <div className='flex gap-2'>
          <button className='btn btn-secondary' onClick={() => void exporting('json')}>
            导出 JSON
          </button>
          <button className='btn btn-secondary' onClick={() => void exporting('csv')}>
            导出 CSV
          </button>
        </div>
      </div>

      {errorMessage ? <FormMessage tone='error' message={errorMessage} /> : null}

      <section className='bg-white rounded-2xl p-5 shadow-card-sm grid grid-cols-2 md:grid-cols-5 gap-3'>
        <MetricCard label='总生成数' value={summary?.totalGenerations ?? 0} />
        <MetricCard label='反馈总数' value={summary?.totalFeedback ?? 0} />
        <MetricCard label='Like' value={summary?.likeCount ?? 0} />
        <MetricCard label='Dislike' value={summary?.dislikeCount ?? 0} />
        <MetricCard label='Like Ratio' value={`${Math.round((summary?.likeRatio ?? 0) * 100)}%`} />
      </section>

      <section className='bg-white rounded-2xl p-5 shadow-card-sm space-y-3'>
        <h2 className='font-semibold text-[1.0625rem] text-apple-near-black'>筛选</h2>
        <form
          className='grid grid-cols-1 md:grid-cols-4 gap-3'
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            setFilters((prev) => ({ ...prev, page: 1 }));
          }}
        >
          <input
            className='input'
            type='date'
            value={filters.dateFrom || ''}
            onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
          />
          <input
            className='input'
            type='date'
            value={filters.dateTo || ''}
            onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
          />
          <input
            className='input'
            placeholder='domain'
            value={filters.domain || ''}
            onChange={(event) => setFilters((prev) => ({ ...prev, domain: event.target.value }))}
          />
          <select
            className='input'
            value={filters.rating || ''}
            onChange={(event) => setFilters((prev) => ({ ...prev, rating: event.target.value as FeedbackFilters['rating'] }))}
          >
            <option value=''>all ratings</option>
            <option value='like'>like</option>
            <option value='dislike'>dislike</option>
          </select>
          <input
            className='input'
            placeholder='templateId'
            value={filters.templateId || ''}
            onChange={(event) => setFilters((prev) => ({ ...prev, templateId: event.target.value }))}
          />
          <input
            className='input'
            placeholder='providerId'
            value={filters.providerId || ''}
            onChange={(event) => setFilters((prev) => ({ ...prev, providerId: event.target.value }))}
          />
          <input
            className='input'
            placeholder='modelId'
            value={filters.modelId || ''}
            onChange={(event) => setFilters((prev) => ({ ...prev, modelId: event.target.value }))}
          />
          <button type='submit' className='btn btn-primary'>
            应用筛选
          </button>
        </form>
      </section>

      <section className='bg-white rounded-2xl p-5 shadow-card-sm space-y-3'>
        <h2 className='font-semibold text-[1.0625rem] text-apple-near-black'>反馈列表</h2>
        {listQuery.isLoading ? <p className='text-[0.875rem] text-apple-gray'>加载中…</p> : null}
        {!listQuery.isLoading ? (
          <div className='overflow-x-auto'>
            <table className='min-w-full text-[0.8125rem]'>
              <thead>
                <tr className='text-left border-b border-apple-border'>
                  <th className='px-3 py-2'>时间</th>
                  <th className='px-3 py-2'>用户</th>
                  <th className='px-3 py-2'>模板</th>
                  <th className='px-3 py-2'>Domain</th>
                  <th className='px-3 py-2'>Provider</th>
                  <th className='px-3 py-2'>Model</th>
                  <th className='px-3 py-2'>Rating</th>
                  <th className='px-3 py-2'>Feedback</th>
                  <th className='px-3 py-2'>Job</th>
                </tr>
              </thead>
              <tbody>
                {(list?.items ?? []).map((row) => (
                  <tr key={row.id} className='border-b border-apple-border/70 align-top'>
                    <td className='px-3 py-2 whitespace-nowrap'>{new Date(row.createdAt).toLocaleString('zh-CN', { hour12: false })}</td>
                    <td className='px-3 py-2'>{row.user?.name || row.user?.id || '-'}</td>
                    <td className='px-3 py-2'>{row.templateName || row.templateId}</td>
                    <td className='px-3 py-2'>{row.domain || '-'}</td>
                    <td className='px-3 py-2'>{row.providerName || row.providerId || '-'}</td>
                    <td className='px-3 py-2'>{row.modelId || '-'}</td>
                    <td className='px-3 py-2'>{row.rating}</td>
                    <td className='px-3 py-2 max-w-[280px] whitespace-pre-wrap break-words'>{row.feedbackText || '-'}</td>
                    <td className='px-3 py-2'>
                      <a href={`/generation-jobs/${row.generationJobId}`} className='btn-ghost'>
                        查看
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <div className='flex items-center justify-between gap-2'>
          <p className='text-[0.8125rem] text-apple-gray'>
            第 {list?.page ?? filters.page} 页 · 共 {list?.total ?? 0} 条
          </p>
          <div className='flex gap-2'>
            <button
              className='btn btn-secondary'
              disabled={(list?.page ?? 1) <= 1}
              onClick={() => setFilters((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
            >
              上一页
            </button>
            <button
              className='btn btn-secondary'
              disabled={(list?.page ?? 1) * (list?.pageSize ?? filters.pageSize) >= (list?.total ?? 0)}
              onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
            >
              下一页
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function MetricCard(props: { label: string; value: string | number }) {
  return (
    <div className='rounded-xl border border-apple-border px-3 py-3'>
      <p className='text-[0.75rem] text-apple-gray mb-1'>{props.label}</p>
      <p className='text-[1.125rem] font-semibold text-apple-near-black'>{props.value}</p>
    </div>
  );
}
